import { Link } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useHelpOptional } from '../../../help/HelpContext';
import { useAlgoDeviceSupport } from '../../Admin/algoDeviceSupport';
import { homeGuidanceSteps, homeSetupStepsVisible } from '../homeSetupGuidance';

const STATUS_LABEL = {
  complete: 'Done',
  'not-configured': 'Not configured',
  loading: 'Checking',
  unavailable: 'Unavailable',
  error: 'Could not check',
};

export default function HomeSetupGuide({
  tenantName,
  role,
  dealerActive,
  progress,
  onDismiss,
  onRetry,
  preparing = false,
  notice = '',
  showSteps = false,
  onEnterSupport = null,
}) {
  const help = useHelpOptional();
  const algoDeviceSupport = useAlgoDeviceSupport();
  const steps = homeSetupStepsVisible({ guide: showSteps, notice })
    ? homeGuidanceSteps({ role, dealerActive, algoDeviceSupport })
    : [];
  const title = tenantName ? `Welcome to ${tenantName}` : 'Welcome';

  return (
    <Box sx={{ px: 2, pt: 2, maxWidth: 720 }} data-testid="home-setup">
      <Typography variant="h5" sx={{ fontWeight: 650, color: '#1f2937' }}>{title}</Typography>
      {preparing ? (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="home-preparing">
          Your administrator is preparing this workspace.
          {help ? (
            <Button size="small" onClick={help.openHelp} sx={{ ml: 1 }}>Help</Button>
          ) : null}
        </Alert>
      ) : null}
      {notice ? (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={onEnterSupport ? (
            <Button color="inherit" size="small" onClick={onEnterSupport}>
              Enter support context
            </Button>
          ) : null}
        >
          {notice}
        </Alert>
      ) : null}
      {!preparing && !notice && !steps.length ? (
        <Typography variant="body2" sx={{ mt: 1, color: '#4b5563' }}>
          Your workspace home is ready.
        </Typography>
      ) : null}
      {!preparing && steps.length ? (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.9)', border: '1px solid #e5e7eb' }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: '#4b5563' }}>
            This home does not run any alerts yet. These steps only open the existing setup pages.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {steps.map((step) => {
              const status = progress?.[step.id];
              const showStatus = !step.hideStatus && status && status !== 'unverified';
              return (
                <Box component="li" key={step.id} sx={{ mb: 0.75 }}>
                  {step.parts ? step.parts.map((part) => (
                    part.to
                      ? <Link key={part.text} to={part.to}>{part.text}</Link>
                      : <span key={part.text}>{part.text}</span>
                  )) : <Link to={step.to}>{step.label}</Link>}
                  {showStatus ? (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: '#6b7280' }}>
                      {STATUS_LABEL[status] || ''}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            {help ? <Button size="small" onClick={help.openHelp}>Help</Button> : null}
            {onRetry ? <Button size="small" onClick={onRetry}>Retry</Button> : null}
            {onDismiss ? <Button size="small" onClick={onDismiss}>Dismiss</Button> : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
